import { supabase } from "@/integrations/supabase/client";
import { ContextPayload, ContextResponse } from "../types";
import { logErrorToast } from "@/utils/toastLogger";

/**
 * Maximum retries for edge function calls
 */
const MAX_RETRIES = 2;

/**
 * Detailed diagnostic logging for edge function calls
 */
function logDiagnostics(functionName: string, attempt: number, error: any) {
  console.group(`Edge Function Diagnostic: ${functionName}`);
  console.log('Attempt:', attempt + 1);
  console.log('Timestamp:', new Date().toISOString());
  console.log('Error type:', error?.constructor?.name);
  console.log('Error message:', error?.message);
  console.log('Error stack:', error?.stack);
  if (error?.response) {
    console.log('Response status:', error.response.status);
    console.log('Response data:', error.response.data);
  }
  console.groupEnd();
}

/**
 * Retry a supabase function call with exponential backoff and diagnostics
 */
async function retryableFunctionCall<T>(
  functionName: string, 
  body: any, 
  retries = MAX_RETRIES
): Promise<{ data: T | null; error: any }> {
  let lastError = null;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Add increasing timeout between retries
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, 200 * Math.pow(2, attempt - 1)));
      }

      console.log(`Calling ${functionName} - Attempt ${attempt + 1}/${retries + 1}`);
      const startTime = performance.now();
      
      const result = await supabase.functions.invoke(functionName, { 
        body,
        headers: {
          'x-diagnostic-attempt': attempt.toString(),
          'x-client-timestamp': new Date().toISOString()
        }
      });

      const duration = performance.now() - startTime;
      console.log(`${functionName} completed in ${duration.toFixed(2)}ms`);
      
      return result;
    } catch (error) {
      lastError = error;
      logDiagnostics(functionName, attempt, error);
    }
  }
  
  return { data: null, error: lastError };
}

/**
 * Save context data to persistent storage with retries and fallback
 * @param payload Context data to save
 * @returns Result of operation
 */
export async function saveContext(payload: ContextPayload): Promise<ContextResponse> {
  try {
    // Check if the user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    
    // If there's no active session, use fallback
    if (!session?.user) {
      console.log("No active session when saving context. Using fallback.");
      return { 
        success: true, 
        key: payload.key,
        fallback: true
      };
    }
    
    try {
      const { data, error } = await retryableFunctionCall<ContextResponse>(
        "saveContext", 
        payload
      );

      if (error) {
        console.error("Context save error:", error);
        // Return graceful fallback response
        return { 
          success: true, // Mark as successful to prevent UI disruption
          key: payload.key, 
          error: error.message,
          fallback: true
        };
      }

      return data || { success: true, key: payload.key };
    } catch (invokeError) {
      console.error("Failed to invoke saveContext edge function:", invokeError);
      // Return a graceful fallback response
      return { 
        success: true, // Mark as successful to prevent UI disruption 
        key: payload.key, 
        error: "Edge function communication error",
        fallback: true
      };
    }
  } catch (error) {
    console.error("Context save exception:", error);
    // Return a graceful fallback response
    return { 
      success: true, // Mark as successful to prevent UI disruption
      key: payload.key, 
      error: error instanceof Error ? error.message : String(error),
      fallback: true
    };
  }
}

/**
 * Load context data from persistent storage with retries and fallback
 * @param key Unique key of the context to load
 * @returns Context data if found
 */
export async function loadContext(key: string): Promise<ContextResponse> {
  try {
    // Check if the user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    
    // If there's no active session, use fallback
    if (!session?.user) {
      console.log("No active session when loading context. Using fallback.");
      return { 
        success: true, 
        key,
        fallback: true
      };
    }
    
    try {
      const { data, error } = await retryableFunctionCall<ContextResponse>(
        "loadContext", 
        { key }
      );

      if (error) {
        console.error("Context load error:", error);
        // Return graceful fallback response
        return { 
          success: true, // Mark as successful to prevent UI disruption
          key, 
          error: error.message,
          fallback: true
        };
      }

      return data || { success: true, key, fallback: true };
    } catch (invokeError) {
      console.error("Failed to invoke loadContext edge function:", invokeError);
      // Return a graceful fallback response
      return { 
        success: true, // Mark as successful to prevent UI disruption
        key, 
        error: "Edge function communication error",
        fallback: true 
      };
    }
  } catch (error) {
    console.error("Context load exception:", error);
    // Return a graceful fallback response
    return { 
      success: true, // Mark as successful to prevent UI disruption 
      key, 
      error: error instanceof Error ? error.message : String(error),
      fallback: true
    };
  }
}

/**
 * Invalidate (delete) context data with retries and fallback
 * @param key Unique key of the context to invalidate
 * @returns Result of operation
 */
export async function invalidateContext(key: string): Promise<ContextResponse> {
  try {
    // Check if the user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    
    // If there's no active session, use fallback
    if (!session?.user) {
      console.log("No active session when invalidating context. Using fallback.");
      return { 
        success: true, 
        key,
        fallback: true 
      };
    }
    
    try {
      const { data, error } = await retryableFunctionCall<ContextResponse>(
        "invalidateContext", 
        { key }
      );

      if (error) {
        console.error("Context invalidate error:", error);
        // Return graceful fallback response
        return { 
          success: true, // Mark as successful to prevent UI disruption
          key, 
          error: error.message,
          fallback: true 
        };
      }

      return data || { success: true, key };
    } catch (invokeError) {
      console.error("Failed to invoke invalidateContext edge function:", invokeError);
      // Return a graceful fallback response
      return { 
        success: true, // Mark as successful to prevent UI disruption
        key, 
        error: "Edge function communication error",
        fallback: true
      };
    }
  } catch (error) {
    console.error("Context invalidate exception:", error);
    // Return a graceful fallback response
    return { 
      success: true, // Mark as successful to prevent UI disruption
      key, 
      error: error instanceof Error ? error.message : String(error),
      fallback: true
    };
  }
}
